import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Check, Clock, Copy, ChevronLeft, ChevronRight, Send, Palette } from "lucide-react";
import api from "@/lib/api";
import { useScope } from "@/context/ScopeContext";
import { BackgroundSettings } from "@/components/BackgroundSettings";
import BudgetMatrix from "@/components/snap/BudgetMatrix";
import BusinessExpenseSummary from "@/components/snap/BusinessExpenseSummary";
import PolicyFlags from "@/components/snap/PolicyFlags";
import QuarterlyBusiness from "@/components/snap/QuarterlyBusiness";
import BillsPanel from "@/components/snap/BillsPanel";
import CategoryInsights from "@/components/snap/CategoryInsights";
import SavingsTracker from "@/components/snap/SavingsTracker";
import CreditScoreTracker from "@/components/snap/CreditScoreTracker";
import SpendTrends from "@/components/snap/SpendTrends";
import AnnualSnapshot from "@/components/snap/AnnualSnapshot";
import RecentBillPayments from "@/components/snap/RecentBillPayments";
import FinancialRadar from "@/components/FinancialRadar";
import SnapPaywall from "@/components/snap/SnapPaywall";
import OnboardingTooltips from "@/components/snap/OnboardingTooltips";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function SnapBudget() {
  const navigate = useNavigate();
  const { setScope } = useScope();
  const now = new Date();
  const [access, setAccess] = useState(null); // null=loading
  const [bgOpen, setBgOpen] = useState(false);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [budget, setBudget] = useState(null);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const [tour, setTour] = useState(false);
  const [billsVersion, setBillsVersion] = useState(0);
  const saveTimer = useRef(null);
  const bizTimer = useRef(null);

  const loadAccess = useCallback(() => {
    api.get("/plans").then((r) => setAccess(r.data.entitlements.snapbudget)).catch(() => setAccess({ has_access: false }));
  }, []);
  useEffect(() => { loadAccess(); }, [loadAccess]);
  useEffect(() => { setScope("snapbudget"); return () => setScope("all"); }, [setScope]);

  const loadBudget = useCallback((m, y) => {
    api.get(`/snapbudget?month=${m}&year=${y}`).then((r) => setBudget(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (access?.has_access) { loadBudget(month, year); }
  }, [access, month, year, loadBudget]);

  useEffect(() => {
    if (access?.has_access && !localStorage.getItem("snap_onboarded")) {
      const t = setTimeout(() => setTour(true), 600);
      return () => clearTimeout(t);
    }
  }, [access]);

  const persist = useCallback((weeks) => {
    setSaveState("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.put("/snapbudget", { month, year, weeks });
        setBudget(data);
        setSaveState("saved");
        const savingsSum = weeks.reduce((s, w) => s + (Number(w.expenses?.savings?.amount) || 0), 0);
        api.put("/profile", { monthly_savings: savingsSum }).catch(() => {});
        setTimeout(() => setSaveState("idle"), 1500);
      } catch (e) {
        setSaveState("idle");
        toast.error("Could not save budget");
      }
    }, 700);
  }, [month, year]);

  const saveBusinessIncomes = useCallback((list) => {
    setSaveState("saving");
    if (bizTimer.current) clearTimeout(bizTimer.current);
    bizTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.put("/snapbudget", { month, year, weeks: budget?.weeks || [], business_incomes: list });
        setBudget(data);
        setSaveState("saved");
        setTimeout(() => setSaveState("idle"), 1500);
      } catch (e) {
        setSaveState("idle");
        toast.error("Could not save business income");
      }
    }, 700);
  }, [month, year, budget]);

  const shiftMonth = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const copyLastMonth = async () => {
    const hasData = budget && (budget.totals?.income > 0 || budget.totals?.expenses > 0);
    if (hasData && !window.confirm(`${MONTHS[month - 1]} ${year} already has numbers. Copying last month will overwrite them. Continue?`)) return;
    try {
      await api.post("/snapbudget/copy", { to_month: month, to_year: year });
      loadBudget(month, year);
      toast.success("Copied last month's budget into this month");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nothing to copy from last month");
    }
  };

  const subscribe = async () => {
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "snapbudget_monthly", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
    }
  };

  const sendTestRadar = async () => {
    try {
      const { data } = await api.post("/snapbudget/radar-test");
      toast.success(data.status === "sent"
        ? `Test radar emailed to ${data.to}`
        : "Radar generated — email is simulated until your SendGrid sender is verified");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send test radar");
    }
  };

  const trialDaysLeft = access?.trial_ends
    ? Math.max(0, Math.ceil((new Date(access.trial_ends) - new Date()) / 86400000))
    : 0;

  const finishTour = () => { localStorage.setItem("snap_onboarded", "1"); setTour(false); };

  return (
    <div className="min-h-screen app-canvas bg-[#070d1c] text-slate-100" style={{ backgroundImage: "radial-gradient(600px circle at 20% 0%, rgba(16,185,129,0.08), transparent 60%)" }}>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#070d1c]/80 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <button data-testid="snap-back" onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-300 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Briefing
          </button>
          <div className="flex items-center gap-2">
            <img src="/snapbudget-logo.webp" alt="SnapBudget" className="w-8 h-8 rounded-lg" />
            <span className="font-bold tracking-wide text-white hidden sm:block">SnapBudget</span>
          </div>
          <div className="flex items-center gap-3 min-w-[90px] justify-end">
            <button
              data-testid="snap-open-background"
              onClick={() => setBgOpen(true)}
              title="Customize background"
              className="flex items-center justify-center w-9 h-9 rounded-lg border border-white/10 bg-white/5 text-slate-300 hover:text-emerald-300 hover:border-emerald-400/40 transition-colors"
            >
              <Palette className="w-4 h-4" />
            </button>
            {saveState === "saving" && <span className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving</span>}
            {saveState === "saved" && <span data-testid="snap-saved" className="text-xs text-emerald-400 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Saved</span>}
            {access?.trialing && (
              <span data-testid="snap-trial-badge" className="hidden sm:flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20">
                <Clock className="w-3 h-3" /> Trial
              </span>
            )}
          </div>
        </div>
      </header>

      {access === null ? (
        <div className="min-h-[70vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
      ) : !access.has_access ? (
        <SnapPaywall access={access} onUnlocked={loadAccess} />
      ) : (
        <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6">
          {access.trialing && (
            <div data-testid="snap-trial-countdown" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 px-5 py-3">
              <p className="text-sm text-amber-200">
                <Clock className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                <span className="font-semibold" data-testid="snap-trial-days">{trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left</span> in your free trial — keep your cashflow command center for just $9/mo.
              </p>
              <button data-testid="snap-trial-upgrade" onClick={subscribe} className="h-9 px-4 rounded-lg bg-amber-400 text-[#241a04] text-sm font-semibold hover:bg-amber-300 transition-colors shrink-0">
                Upgrade now
              </button>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-1">
              <button data-testid="snap-month-prev" onClick={() => shiftMonth(-1)} className="w-9 h-9 grid place-items-center rounded-lg bg-white/5 hover:bg-emerald-500/20 text-emerald-200 transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span data-testid="snap-month-label" className="px-3 min-w-[150px] text-center font-semibold text-white">{MONTHS[month - 1]} {year}</span>
              <button data-testid="snap-month-next" onClick={() => shiftMonth(1)} className="w-9 h-9 grid place-items-center rounded-lg bg-white/5 hover:bg-emerald-500/20 text-emerald-200 transition-colors">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="snap-radar-test" onClick={sendTestRadar} className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/15 text-slate-300 text-sm hover:bg-white/5 transition-colors">
                <Send className="w-4 h-4" /> Email test radar
              </button>
              <button data-testid="snap-copy-last" onClick={copyLastMonth} className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-emerald-500/30 text-emerald-300 text-sm hover:bg-emerald-500/10 transition-colors">
                <Copy className="w-4 h-4" /> Copy last month
              </button>
            </div>
          </div>

          {budget && <BudgetMatrix budget={budget} onChange={persist} />}

          {budget && (
            <BusinessExpenseSummary
              business={budget.business}
              incomes={budget.business_incomes}
              month={month}
              year={year}
              onIncomesChange={saveBusinessIncomes}
            />
          )}

          {budget && <PolicyFlags refreshKey={budget?.business?.expenses} />}

          {budget && <QuarterlyBusiness year={year} refreshKey={budget?.business?.net} />}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">{budget && <CategoryInsights budget={budget} />}</div>
            <div className="lg:col-span-1">{budget && <SavingsTracker budget={budget} />}</div>
          </div>

          <CreditScoreTracker />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SpendTrends refreshKey={budget?.totals?.net} />
            <AnnualSnapshot refreshKey={budget?.totals?.net} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <FinancialRadar signature={billsVersion} />
            <RecentBillPayments refreshKey={`${billsVersion}-${budget?.bill_ledger?.length || 0}`} />
          </div>
          <BillsPanel onChange={() => setBillsVersion((v) => v + 1)} />
        </main>
      )}

      {tour && <OnboardingTooltips onDone={finishTour} />}
      <BackgroundSettings open={bgOpen} onClose={() => setBgOpen(false)} scope="snapbudget" />
    </div>
  );
}
